import { useMemo } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import type {
  InteractiveCanvasComponents,
  PrioritisationT,
} from '@mentimeter/question-modules-types';
import { NonInteractiveVisualizationWrapper } from '../../shared/components/NonInteractiveVisualizationWrapper';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { PrioritisationVisualization } from './PrioritisationVisualization';

interface PrioritisationChoice {
  id: number;
  label: string;
}

interface PrioritisationPresentationProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  shouldHideResults: boolean;
  choices: PrioritisationChoice[];
  shouldAnimate: boolean;
  results: PrioritisationT['results'];
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
}

export function PrioritisationPresentation({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  choices,
  shouldAnimate,
  shouldHideResults,
  results,
  CanvasVisualizationArea,
}: PrioritisationPresentationProps) {
  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  const resultsWithFallback = useMemo(() => {
    const hasScores = results.findIndex((d) => d.score) > -1;
    const resultsBeforeSorting = choices.map((choice, index) => {
      const result = results.find((result) => result.id === choice.id);
      const fallbackResult = {
        id: choice.id,
        label: choice.label,
        score: 0,
      };
      return { ...(result ?? fallbackResult), index }; // add index to keep same fillcolor mapping after sorting
    });

    if (!hasScores) return resultsBeforeSorting;
    return resultsBeforeSorting.sort((a, b) => b.score - a.score);
  }, [choices, results]);

  return (
    <Box width="100%" height="100%" data-testid="100-points-container">
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />
      <ConditionalWrapper
        width="100%"
        height="100%"
        flex="1 1 auto"
        justifyContent="center"
        alignItems="center"
      >
        <PrioritisationVisualization
          shouldHideResults={shouldHideResults}
          shouldAnimate={shouldAnimate}
          results={resultsWithFallback}
        />
      </ConditionalWrapper>
    </Box>
  );
}
